import { DesktopHeader, RightSidebarSkeleton, MobileHeader } from "./HomePageSkeleton.jsx";

const S = ({ className = "" }) => (
    <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

// ─── Cover Banner + Profile Info Card ────────────────────────────────────────
const ProfileHeroSkeleton = () => (
    <div className="bg-white rounded-xl shadow-sm overflow-visible">
        <S className="w-full h-28 rounded-t-xl rounded-b-none bg-orange-200" />

        <div className="px-4 pb-4">
            <div className="flex items-end justify-between -mt-8 mb-3">
                <S className="w-16 h-16 rounded-full border-4 border-white shrink-0" />
                <S className="w-7 h-7 rounded-full mt-10" />
            </div>

            <S className="w-44 h-5 mb-2" />
            <S className="w-24 h-3.5 mb-2 bg-orange-100" />
            <div className="flex items-center gap-2">
                <S className="w-3 h-3 rounded-full" />
                <S className="w-48 h-3" />
            </div>
        </div>
    </div>
);

// ─── Stats Row ────────────────────────────────────────────────────────────────
const StatsRowSkeleton = () => (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="flex items-center divide-x divide-gray-100">
            {[...Array(3)].map((_, i) => (
                <div key={i} className="flex-1 flex items-center gap-3 px-4 py-3">
                    <S className="w-8 h-8 rounded-md shrink-0 bg-orange-100" />
                    <div className="flex flex-col gap-1.5">
                        <S className="w-20 h-4" />
                        <S className="w-24 h-2.5" />
                    </div>
                </div>
            ))}
        </div>
    </div>
);

// ─── Tab Navigation ───────────────────────────────────────────────────────────
const TabNavSkeleton = () => (
    <div className="bg-white rounded-xl shadow-sm px-2">
        <div className="flex items-center gap-1 overflow-x-auto">
            <div className="flex flex-col items-center px-3 py-3 shrink-0">
                <S className="w-12 h-3.5 bg-orange-200" />
                <div className="mt-2 w-full h-0.5 bg-orange-400 rounded-full animate-pulse" />
            </div>
            {[80, 64, 96, 80, 80, 48].map((w, i) => (
                <div key={i} className="px-3 py-3 shrink-0">
                    <S className={`w-${w === 80 ? 20 : w === 64 ? 16 : w === 96 ? 24 : w === 48 ? 12 : 20} h-3.5`}
                        style={{ width: w }} />
                </div>
            ))}
        </div>
    </div>
);

// ─── About + Bio Section ──────────────────────────────────────────────────────
const AboutSectionSkeleton = () => (
    <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
            <S className="w-16 h-4" />
            <S className="w-16 h-7 rounded-md" />
        </div>
        <S className="w-32 h-3" />
    </div>
);

// ─── Blog Categories Section ──────────────────────────────────────────────────
const BlogCategoriesSkeleton = () => (
    <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
            <S className="w-32 h-4" />
            <S className="w-28 h-7 rounded-md" />
        </div>
        <S className="w-36 h-3" />
    </div>
);

// ─── Detail Card ──────────────────────────────────────────────────────────────
const DetailCardSkeleton = ({ fields = 2 }) => (
    <div className="bg-white rounded-xl shadow-sm p-4 flex flex-col gap-3">
        <div className="flex items-center gap-2 pb-1 border-b border-gray-100">
            <S className="w-5 h-5 rounded-full bg-orange-100 shrink-0" />
            <S className="w-32 h-3.5" />
        </div>

        {[...Array(fields)].map((_, i) => (
            <div key={i} className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 flex-1">
                    <S className="w-4 h-4 rounded-full shrink-0" />
                    <div className="flex flex-col gap-1.5 flex-1">
                        <S className="w-20 h-2.5" />  {/* label */}
                        <S className="w-28 h-3.5" />  {/* value */}
                    </div>
                </div>
                <S className="w-4 h-4 rounded-sm shrink-0" />  {/* edit icon */}
            </div>
        ))}
    </div>
);

// ─── Details Grid (3×2) ───────────────────────────────────────────────────────
const DetailsGridSkeleton = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <DetailCardSkeleton fields={4} /> {/* Personal Details */}
        <DetailCardSkeleton fields={2} /> {/* Contact Details */}
        <DetailCardSkeleton fields={2} /> {/* Professional Details */}
        <DetailCardSkeleton fields={2} /> {/* Family Details */}
        <DetailCardSkeleton fields={3} /> {/* Location Details */}
        <DetailCardSkeleton fields={1} /> {/* Privacy Details */}
    </div>
);

// ─── Root ─────────────────────────────────────────────────────────────────────
const UserProfileSkeleton = () => (
    <div className="min-h-screen bg-gray-50">
        <DesktopHeader />
        <MobileHeader />
        <div className="max-w-[1200px] mx-auto flex gap-4 px-4 pt-4 pb-8 items-start">
            <main className="flex-1 min-w-0 flex flex-col gap-3">
                <ProfileHeroSkeleton />
                <StatsRowSkeleton />
                <TabNavSkeleton />

                <div className="bg-white rounded-xl shadow-sm p-5 flex flex-col gap-6">
                    <AboutSectionSkeleton />
                    <div className="border-t border-gray-100" />
                    <BlogCategoriesSkeleton />
                    <div className="border-t border-gray-100" />
                    <DetailsGridSkeleton />
                </div>
            </main>

            <RightSidebarSkeleton />
        </div>
    </div>
);

export default UserProfileSkeleton;